"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const SCREEN_ORDERS = [1, 2, 3];
const AUDIENCE_OPTIONS = [
  { value: "influencer", label: "Influencers" },
  { value: "business", label: "Businesses" },
  { value: "user", label: "Users" },
];

const DEFAULT_HEADER_BACKGROUND_URL =
  "https://images.unsplash.com/photo-1709477992469-6bec3a5c2e64?q=80&w=3270&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";

const createEmptyScreen = (order, audience) => ({
  id: null,
  order,
  title: "",
  description: "",
  imageUrlInput: "",
  existingImageUrl: "",
  file: null,
  localPreviewUrl: null,
  is_active: true,
  saving: false,
  audience,
});

const createEmptyHeaderBackground = (audience) => ({
  audience,
  imageUrlInput: "",
  existingImageUrl: DEFAULT_HEADER_BACKGROUND_URL,
  file: null,
  localPreviewUrl: null,
  saving: false,
  source: "default",
});

const OnboardingScreens = () => {
  const [selectedAudience, setSelectedAudience] = useState("influencer");
  const [screens, setScreens] = useState(
    SCREEN_ORDERS.map((order) => createEmptyScreen(order, "influencer"))
  );
  const [baselineScreens, setBaselineScreens] = useState(
    SCREEN_ORDERS.map((order) => createEmptyScreen(order, "influencer"))
  );
  const [headerBackground, setHeaderBackground] = useState(
    createEmptyHeaderBackground("influencer")
  );
  const [baselineHeaderBackground, setBaselineHeaderBackground] = useState(
    createEmptyHeaderBackground("influencer")
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const objectUrlsRef = useRef([]);

  const revokeObjectUrl = useCallback(
    (url) => {
      if (!url) {
        return;
      }

      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        console.warn("Failed to revoke object URL", error);
      }

      objectUrlsRef.current = objectUrlsRef.current.filter(
        (item) => item !== url
      );
    },
    [objectUrlsRef]
  );

  useEffect(() => {
    const audienceLabel =
      AUDIENCE_OPTIONS.find((option) => option.value === selectedAudience)
        ?.label || "Onboarding";
    document.title = `Onboarding Screens (${audienceLabel}) | Tafaseel Admin`;
  }, [selectedAudience]);

  const clearObjectUrls = useCallback(() => {
    objectUrlsRef.current.forEach((url) => revokeObjectUrl(url));
    objectUrlsRef.current = [];
  }, [revokeObjectUrl]);

  const fetchScreens = useCallback(
    async ({ initial = false, silent = false, audience } = {}) => {
      const audienceValue = (audience ?? selectedAudience).toLowerCase();

      if (initial) {
        setLoading(true);
      } else if (!silent) {
        setRefreshing(true);
      }

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token missing. Please log in again.");
        }

        Api.defaults.headers.common["Authorization"] = "Bearer " + token;
        const response = await Api.get("/admin/onboarding-screens", {
          params: { audience: audienceValue },
        });

        if (!response.data.success) {
          throw new Error(
            response.data.message || "Failed to load onboarding screens"
          );
        }

        const records = response.data.data || [];
        clearObjectUrls();

        const normalized = SCREEN_ORDERS.map((order) => {
          const record =
            records.find((item) => Number(item.order) === Number(order)) ||
            null;

          return {
            id: record?.id ?? null,
            order,
            title: record?.title ?? "",
            description: record?.description ?? "",
            imageUrlInput: record?.image_url ?? "",
            existingImageUrl: record?.image_url ?? "",
            file: null,
            localPreviewUrl: null,
            is_active:
              typeof record?.is_active === "boolean" ? record.is_active : true,
            saving: false,
            audience: audienceValue,
          };
        });

        setScreens(normalized);
        setBaselineScreens(
          normalized.map((screen) => ({
            ...screen,
            file: null,
            localPreviewUrl: null,
          }))
        );
      } catch (error) {
        console.error("Error fetching onboarding screens:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch onboarding screens"
        );
      } finally {
        if (initial) {
          setLoading(false);
        } else if (!silent) {
          setRefreshing(false);
        }
      }
    },
    [clearObjectUrls, selectedAudience]
  );

  const fetchHeaderBackground = useCallback(
    async ({ audience, silent = false } = {}) => {
      const audienceValue = (audience ?? selectedAudience).toLowerCase();

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token missing. Please log in again.");
        }

        Api.defaults.headers.common["Authorization"] = "Bearer " + token;
        const response = await Api.get("/admin/home-header-background", {
          params: { audience: audienceValue },
        });

        const data = response.data?.data || {};
        const imageUrl = data.image_url || DEFAULT_HEADER_BACKGROUND_URL;
        const source = data.source || "default";
        const usesUploadedFile = Boolean(data.uses_uploaded_file);

        setHeaderBackground({
          audience: audienceValue,
          imageUrlInput:
            !usesUploadedFile && source !== "default" ? imageUrl : "",
          existingImageUrl: imageUrl,
          file: null,
          localPreviewUrl: null,
          saving: false,
          source,
        });

        setBaselineHeaderBackground({
          audience: audienceValue,
          imageUrlInput:
            !usesUploadedFile && source !== "default" ? imageUrl : "",
          existingImageUrl: imageUrl,
          file: null,
          localPreviewUrl: null,
          saving: false,
          source,
        });
      } catch (error) {
        console.error("Error fetching header background:", error);
        if (!silent) {
          toast.error(
            error.response?.data?.message ||
              error.message ||
              "Failed to fetch header background image"
          );
        }

        setHeaderBackground((prev) => ({
          ...createEmptyHeaderBackground(audienceValue),
          saving: false,
        }));
        setBaselineHeaderBackground(createEmptyHeaderBackground(audienceValue));
      }
    },
    [selectedAudience]
  );

  useEffect(() => {
    fetchScreens({ initial: true, audience: selectedAudience });
    fetchHeaderBackground({ audience: selectedAudience, silent: true });

    return () => {
      clearObjectUrls();
    };
  }, [fetchScreens, fetchHeaderBackground, clearObjectUrls, selectedAudience]);

  const updateScreen = (order, updater) => {
    setScreens((prev) =>
      prev.map((screen) => (screen.order === order ? updater(screen) : screen))
    );
  };

  const handleAudienceChange = (event) => {
    const value = event.target.value;
    clearObjectUrls();
    setSelectedAudience(value);
    setScreens(SCREEN_ORDERS.map((order) => createEmptyScreen(order, value)));
    setBaselineScreens(
      SCREEN_ORDERS.map((order) => createEmptyScreen(order, value))
    );
    setHeaderBackground(createEmptyHeaderBackground(value));
    setBaselineHeaderBackground(createEmptyHeaderBackground(value));
  };

  const handleFieldChange = (order, field, value) => {
    updateScreen(order, (screen) => ({
      ...screen,
      [field]: value,
    }));
  };

  const handleImageUrlChange = (order, value) => {
    updateScreen(order, (screen) => {
      if (screen.localPreviewUrl) {
        try {
          URL.revokeObjectURL(screen.localPreviewUrl);
        } catch (error) {
          console.warn("Failed to revoke local preview URL", error);
        }
        objectUrlsRef.current = objectUrlsRef.current.filter(
          (url) => url !== screen.localPreviewUrl
        );
      }

      return {
        ...screen,
        imageUrlInput: value,
        file: null,
        localPreviewUrl: null,
      };
    });
  };

  const handleHeaderBackgroundImageUrlChange = (value) => {
    setHeaderBackground((prev) => {
      if (prev.localPreviewUrl) {
        revokeObjectUrl(prev.localPreviewUrl);
      }

      return {
        ...prev,
        imageUrlInput: value,
        file: null,
        localPreviewUrl: null,
        source: value ? "url" : "default",
      };
    });
  };

  const handleHeaderBackgroundFileChange = (event) => {
    const file = event.target.files?.[0];
    if (event.target) {
      event.target.value = "";
    }

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    objectUrlsRef.current.push(previewUrl);

    setHeaderBackground((prev) => {
      if (prev.localPreviewUrl) {
        revokeObjectUrl(prev.localPreviewUrl);
      }

      return {
        ...prev,
        file,
        localPreviewUrl: previewUrl,
        imageUrlInput: "",
        source: "upload",
      };
    });
  };

  const handleResetHeaderBackground = () => {
    setHeaderBackground((prev) => {
      if (prev.localPreviewUrl) {
        revokeObjectUrl(prev.localPreviewUrl);
      }

      return {
        ...baselineHeaderBackground,
        file: null,
        localPreviewUrl: null,
        saving: false,
      };
    });
  };

  const handleSaveHeaderBackground = async () => {
    const currentHeader = headerBackground;
    const audienceValue = (
      currentHeader.audience || selectedAudience
    ).toLowerCase();
    const audienceLabel =
      AUDIENCE_OPTIONS.find((option) => option.value === audienceValue)
        ?.label || audienceValue;
    const trimmedUrl = currentHeader.imageUrlInput.trim();

    if (!currentHeader.file && !trimmedUrl) {
      toast.error("Please upload an image or provide an image URL");
      return;
    }

    setHeaderBackground((prev) => ({
      ...prev,
      saving: true,
    }));

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token missing. Please log in again.");
      }

      const formData = new FormData();
      formData.append("audience", audienceValue);

      if (currentHeader.file) {
        formData.append("image", currentHeader.file);
      } else if (trimmedUrl) {
        formData.append("image_url", trimmedUrl);
      }

      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.post(
        "/admin/home-header-background",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message || "Failed to save header background image"
        );
      }

      toast.success(
        `Home header background for ${audienceLabel} saved successfully`
      );
      if (currentHeader.localPreviewUrl) {
        revokeObjectUrl(currentHeader.localPreviewUrl);
      }
      await fetchHeaderBackground({ audience: audienceValue, silent: true });
    } catch (error) {
      console.error("Error saving header background:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save header background image"
      );
    } finally {
      setHeaderBackground((prev) => ({
        ...prev,
        saving: false,
      }));
    }
  };

  const handleFileChange = (order, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    objectUrlsRef.current.push(previewUrl);

    updateScreen(order, (screen) => {
      if (screen.localPreviewUrl) {
        try {
          URL.revokeObjectURL(screen.localPreviewUrl);
        } catch (error) {
          console.warn("Failed to revoke previous preview URL", error);
        }
        objectUrlsRef.current = objectUrlsRef.current.filter(
          (url) => url !== screen.localPreviewUrl
        );
      }

      return {
        ...screen,
        file,
        localPreviewUrl: previewUrl,
      };
    });
  };

  const handleReset = (order) => {
    const baseline =
      baselineScreens.find(
        (screen) =>
          screen.order === order && screen.audience === selectedAudience
      ) || createEmptyScreen(order, selectedAudience);

    updateScreen(order, (screen) => {
      if (screen.localPreviewUrl) {
        try {
          URL.revokeObjectURL(screen.localPreviewUrl);
        } catch (error) {
          console.warn("Failed to revoke preview URL during reset", error);
        }
        objectUrlsRef.current = objectUrlsRef.current.filter(
          (url) => url !== screen.localPreviewUrl
        );
      }

      return {
        ...baseline,
        file: null,
        localPreviewUrl: null,
        audience: selectedAudience,
      };
    });
  };

  const handleSave = async (order) => {
    const screen = screens.find((item) => item.order === order);
    if (!screen) {
      return;
    }

    const audienceValue = (screen.audience || selectedAudience).toLowerCase();
    const audienceLabel =
      AUDIENCE_OPTIONS.find((option) => option.value === audienceValue)
        ?.label || audienceValue;

    const trimmedTitle = screen.title.trim();
    const trimmedDescription = screen.description.trim();
    const trimmedImageUrl = screen.imageUrlInput.trim();

    if (!trimmedTitle) {
      toast.error(`Screen ${order}: title is required`);
      return;
    }

    if (!trimmedDescription) {
      toast.error(`Screen ${order}: description is required`);
      return;
    }

    if (!screen.id && !screen.file && !trimmedImageUrl) {
      toast.error(
        `Screen ${order}: please upload an image or provide an image URL`
      );
      return;
    }

    updateScreen(order, (current) => ({
      ...current,
      saving: true,
    }));

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token missing. Please log in again.");
      }

      const formData = new FormData();
      formData.append("title", trimmedTitle);
      formData.append("description", trimmedDescription);
      formData.append("order", screen.order);
      formData.append("is_active", screen.is_active ? 1 : 0);
      formData.append("audience", audienceValue);

      if (screen.file) {
        formData.append("image", screen.file);
      } else if (trimmedImageUrl) {
        formData.append("image_url", trimmedImageUrl);
      }

      if (screen.id) {
        formData.append("_method", "PUT");
      }

      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const endpoint = screen.id
        ? `/admin/onboarding-screens/${screen.id}`
        : "/admin/onboarding-screens";

      const response = await Api.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to save onboarding screen"
        );
      }

      toast.success(`Screen ${order} for ${audienceLabel} saved successfully`);
      await fetchScreens({ silent: true, audience: audienceValue });
    } catch (error) {
      console.error(`Error saving screen ${order}:`, error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          `Failed to save screen ${order}`
      );
    } finally {
      updateScreen(order, (current) => ({
        ...current,
        saving: false,
      }));
    }
  };

  const handleRefresh = () => {
    fetchScreens({ audience: selectedAudience });
    fetchHeaderBackground({ audience: selectedAudience });
  };

  const headerPreviewUrl =
    headerBackground.localPreviewUrl ||
    headerBackground.imageUrlInput ||
    headerBackground.existingImageUrl ||
    DEFAULT_HEADER_BACKGROUND_URL;

  if (loading) {
    return (
      <Container fluid>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <Spinner color="primary" />
        </div>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Tafaseel Admin"
            breadcrumbItem="Onboarding Screens"
          />

          <Row className="g-3 mb-3">
            <Col md={6}>
              <FormGroup>
                <Label className="form-label">Audience</Label>
                <Input
                  type="select"
                  value={selectedAudience}
                  onChange={handleAudienceChange}
                >
                  {AUDIENCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col
              md={6}
              className="d-flex align-items-end justify-content-end mt-3 mt-md-0"
            >
              <Button
                color="primary"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <i className="ri-refresh-line align-bottom me-1"></i>
                    Refresh
                  </>
                )}
              </Button>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={6} md={8}>
              <Card className="h-100">
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="card-title mb-0">
                        Home Header Background
                      </h5>
                      <small className="text-muted">
                        Manage the image behind the home screen header
                      </small>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <Table responsive bordered className="align-middle mb-0">
                    <tbody>
                      <tr>
                        <th scope="row" style={{ width: "30%" }}>
                          Preview
                        </th>
                        <td>
                          <div className="d-flex flex-column gap-2">
                            <div className="border rounded p-2 text-center">
                              {headerPreviewUrl ? (
                                <img
                                  src={headerPreviewUrl}
                                  alt="Home header background preview"
                                  className="img-fluid rounded"
                                  style={{
                                    maxHeight: "220px",
                                    objectFit: "cover",
                                    width: "100%",
                                  }}
                                  onError={(event) => {
                                    event.currentTarget.onerror = null;
                                    event.currentTarget.src =
                                      "/placeholder.svg";
                                  }}
                                />
                              ) : (
                                <div className="text-muted py-5">
                                  <i className="ri-image-line fs-1 d-block mb-2"></i>
                                  No image selected
                                </div>
                              )}
                            </div>
                            <FormText color="muted">
                              Recommended size: wide landscape image (16:9 or
                              similar).
                            </FormText>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Image Source</th>
                        <td>
                          <FormGroup className="mb-3">
                            <Label className="form-label">Upload Image</Label>
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={handleHeaderBackgroundFileChange}
                              disabled={headerBackground.saving}
                            />
                            <FormText color="muted">
                              JPG or PNG, up to 5MB.
                            </FormText>
                          </FormGroup>
                          <FormGroup className="mb-0">
                            <Label className="form-label">Or Image URL</Label>
                            <Input
                              type="url"
                              value={headerBackground.imageUrlInput}
                              onChange={(event) =>
                                handleHeaderBackgroundImageUrlChange(
                                  event.target.value
                                )
                              }
                              placeholder="https://example.com/background.jpg"
                              disabled={headerBackground.saving}
                            />
                          </FormGroup>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Actions</th>
                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            <Button
                              color="primary"
                              onClick={handleSaveHeaderBackground}
                              disabled={headerBackground.saving}
                            >
                              {headerBackground.saving ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <i className="ri-save-line me-1"></i>
                                  Save Background
                                </>
                              )}
                            </Button>
                            <Button
                              color="secondary"
                              outline
                              onClick={handleResetHeaderBackground}
                              disabled={headerBackground.saving}
                            >
                              Reset
                            </Button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            {screens.map((screen) => {
              const previewUrl =
                screen.localPreviewUrl ||
                screen.imageUrlInput ||
                screen.existingImageUrl;

              return (
                <Col lg={4} md={6} key={screen.order} className="mb-4">
                  <Card className="h-100">
                    <CardHeader>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="card-title mb-0">
                            Onboarding Screen {screen.order}
                          </h5>
                          <small className="text-muted">
                            Configure title, image, and description
                          </small>
                        </div>
                        {screen.id ? (
                          <span className="badge bg-success-subtle text-success">
                            Saved
                          </span>
                        ) : (
                          <span className="badge bg-warning-subtle text-warning">
                            New
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Table responsive bordered className="align-middle mb-0">
                        <tbody>
                          <tr>
                            <th scope="row" style={{ width: "30%" }}>
                              Title
                            </th>
                            <td>
                              <Input
                                type="text"
                                value={screen.title}
                                onChange={(event) =>
                                  handleFieldChange(
                                    screen.order,
                                    "title",
                                    event.target.value
                                  )
                                }
                                placeholder="Enter onboarding title"
                                disabled={screen.saving}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">Description</th>
                            <td>
                              <Input
                                type="textarea"
                                rows="3"
                                value={screen.description}
                                onChange={(event) =>
                                  handleFieldChange(
                                    screen.order,
                                    "description",
                                    event.target.value
                                  )
                                }
                                placeholder="Enter onboarding description"
                                disabled={screen.saving}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">Image</th>
                            <td>
                              <div className="d-flex flex-column gap-3">
                                <div className="border rounded p-2 text-center">
                                  {previewUrl ? (
                                    <img
                                      src={previewUrl}
                                      alt={`Onboarding screen ${screen.order}`}
                                      className="img-fluid rounded"
                                      style={{
                                        maxHeight: "180px",
                                        objectFit: "cover",
                                      }}
                                      onError={(event) => {
                                        event.currentTarget.onerror = null;
                                        event.currentTarget.src =
                                          "/placeholder.svg";
                                      }}
                                    />
                                  ) : (
                                    <div className="text-muted py-5">
                                      <i className="ri-image-line fs-1 d-block mb-2"></i>
                                      No image selected
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <FormGroup className="mb-3">
                                    <Label className="form-label">
                                      Upload Image
                                    </Label>
                                    <Input
                                      type="file"
                                      accept="image/*"
                                      onChange={(event) =>
                                        handleFileChange(screen.order, event)
                                      }
                                      disabled={screen.saving}
                                    />
                                    <FormText color="muted">
                                      JPG or PNG, up to 5MB.
                                    </FormText>
                                  </FormGroup>
                                  <FormGroup className="mb-0">
                                    <Label className="form-label">
                                      Or Image URL
                                    </Label>
                                    <Input
                                      type="url"
                                      value={screen.imageUrlInput}
                                      onChange={(event) =>
                                        handleImageUrlChange(
                                          screen.order,
                                          event.target.value
                                        )
                                      }
                                      placeholder="https://example.com/image.jpg"
                                      disabled={screen.saving}
                                    />
                                  </FormGroup>
                                </div>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">Actions</th>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                <Button
                                  color="primary"
                                  onClick={() => handleSave(screen.order)}
                                  disabled={screen.saving}
                                >
                                  {screen.saving ? (
                                    <>
                                      <Spinner size="sm" className="me-2" />
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-save-line me-1"></i>
                                      Save Screen
                                    </>
                                  )}
                                </Button>
                                <Button
                                  color="secondary"
                                  outline
                                  onClick={() => handleReset(screen.order)}
                                  disabled={screen.saving}
                                >
                                  Reset
                                </Button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OnboardingScreens;
